<template>
  <div class="flex min-h-full flex-1 flex-col justify-center pt-24 sm:px-6 lg:px-8">
    <div class="sm:mx-auto sm:w-full sm:max-w-md">
      <img class="mx-auto" src="/assets/images/Knocknoc_long_darkbg_Large_redish.png" alt="Knocknoc logo" />
    </div>

    <div class="mt-6 flex flex-col gap-6 sm:mx-auto sm:w-full sm:max-w-sm">
      <span class="text-center text-xl font-semibold tracking-tight">{{ t("msg.setUp2fa") }}</span>

      <FormKit
        type="form"
        :actions="false"
        @submit="handleVerify"
        @submit-invalid="globals.focusFirstError"
        :errors="[errorMessage].filter(Boolean)"
      >
        <div class="mb-4 flex flex-col items-center gap-4">
          <span class="text-center text-sm">{{ t("msg.scanQr") }}</span>

          <canvas ref="qrCanvas"></canvas>

          <div class="flex flex-col items-center">
            <span>Or copy code:</span>
            <CopySpan data-testid="totp-code" class="text-sm" :value="secretKey" />
          </div>
        </div>

        <FormKit
          type="text"
          :classes="{
            outer: '!max-w-full',
          }"
          :label="t('msg.code')"
          name="Code"
          validation="required"
          autocomplete="off"
        />

        <FormKit
          type="submit"
          :classes="{
            outer: '$reset',
            input:
              '$reset inline-flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:h-4 [&_svg]:w-4 [&_svg]:shrink-0 bg-primary text-primary-foreground shadow hover:bg-primary/90 h-9 px-4 py-2',
          }"
          :disabled="isSubmitting || disabled"
        >
          <font-awesome-icon v-if="isSubmitting" icon="fa-sharp-duotone fa-solid fa-spinner-third" spin />
          {{ t("msg.verify") }}
        </FormKit>
      </FormKit>

      <div class="flex flex-col gap-6">
        <button
          v-if="adminStore.user?.TotpEnrolmentDeferable"
          type="button"
          @click.prevent="deferTotpEnrolment"
          class="inline-flex h-10 items-center justify-center gap-2 whitespace-nowrap rounded-md border border-input bg-background px-4 py-2 text-sm font-medium ring-offset-background transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0"
        >
          {{ t("Do this next time") }}
        </button>

        <button
          type="button"
          @click="logout"
          class="inline-flex h-10 items-center justify-center gap-2 whitespace-nowrap rounded-md px-4 py-2 text-sm font-medium text-primary underline-offset-4 ring-offset-background transition-colors hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0"
        >
          {{ t("msg.backToLogin") }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import QRCode from "qrcode";
import { onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";

import CopySpan from "../../components/CopySpan.vue";
import authService from "../../services/auth.service.ts";
import globals from "../../globals";
import { useAdminStore } from "../../pinia/admin.ts";

const { t } = useI18n();

const adminStore = useAdminStore();
adminStore.fetchStatus({ force: true });

const router = useRouter();

const errorMessage = ref("");

const qrCanvas = ref(null);

const secretKey = ref("");
const secretUrl = ref("");

const isSubmitting = ref(false);

async function handleVerify(fields) {
  isSubmitting.value = true;

  try {
    await authService.adminTwoFactorVerify(JSON.stringify(fields));
    await adminStore.fetchStatus({ force: true });

    router.push({ name: "AdminHome" });
  } catch (error) {
    errorMessage.value = globals.renderError(t, error);
  } finally {
    isSubmitting.value = false;
  }
}

const logout = async () => {
  await adminStore.logout();
  router.push({ name: "AdminLogin" });
};

const deferTotpEnrolment = async () => {
  try {
    await authService.adminTwoFactorVerify({ deferEnrolment: true });
    await adminStore.fetchStatus({ force: true });
    router.push({ name: "AdminHome" });
  } catch (error) {
    errorMessage.value = globals.renderError(t, error);
  }
};

onMounted(() => {
  authService.adminTwoFactorOnboard().then(
    (response) => {
      secretKey.value = response.Secret;
      secretUrl.value = response.URL;

      QRCode.toCanvas(qrCanvas.value, secretUrl.value, function (error) {
        if (error) console.error(error);
      });
    },
    (error) => {
      errorMessage.value = globals.renderError(t, error);
    },
  );
});
</script>
